import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
  Grid,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import EmailIcon from '@mui/icons-material/Email';
import { 
  solicitarCodigoAcessoDocumentos, 
  validarCodigoAcessoDocumentos 
} from '../../services/api';
import toast from 'react-hot-toast';

const DocumentAuthSimple: React.FC = () => {
  const [step, setStep] = useState<'cpf' | 'codigo'>('cpf');
  const [cpf, setCpf] = useState('');
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [servidorInfo, setServidorInfo] = useState<any>(null);

  // ── CPF formatter ─────────────────────────────────────────────────────────
  const formatCPF = (value: string) => {
    const n = value.replace(/\D/g, '');
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0,3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6)}`;
    return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9,11)}`;
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(formatCPF(e.target.value));
    setError('');
  };

  // ── Code inputs ───────────────────────────────────────────────────────────
  const handleCodigoChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const next = [...codigo];
      next[index] = value;
      setCodigo(next);
      setError('');
      if (value && index < 5) {
        document.getElementById(`code-input-${index + 1}`)?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      document.getElementById(`code-input-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted.length) return;
    const next = ['', '', '', '', '', ''];
    pasted.split('').forEach((c, i) => { if (i < 6) next[i] = c; });
    setCodigo(next);
    document.getElementById(`code-input-${Math.min(pasted.length, 5)}`)?.focus();
  };

  // ── Step 1: request code (apenas CPF) ─────────────────────────────────────
  const handleSolicitarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedCPF = cpf.replace(/\D/g, '');
    if (cleanedCPF.length !== 11) {
      setError('CPF inválido. Digite 11 números.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      toast.loading('Enviando código para seu email...', { id: 'send-code' });
      // Agora a função espera apenas o CPF (telefone será obtido do banco)
      const response = await solicitarCodigoAcessoDocumentos(cleanedCPF);
      setServidorInfo(response.servidor);
      setStep('codigo');
      toast.success(`Código enviado para: ${response.servidor.email}`, { id: 'send-code' });
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erro ao enviar código';
      setError(msg);
      toast.error(msg, { id: 'send-code' });
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: validate code ─────────────────────────────────────────────────
  const handleValidarCodigo = async (e: React.FormEvent) => {
    e.preventDefault();
    const codigoCompleto = codigo.join('');
    if (codigoCompleto.length !== 6) {
      setError('Digite todos os 6 dígitos do código');
      return;
    }
    setError('');
    setLoading(true);
    try {
      toast.loading('Validando código...', { id: 'validate-code' });
      const response = await validarCodigoAcessoDocumentos(codigoCompleto);

      if (response.session_token) {
        localStorage.setItem('doc_session_token', response.session_token);
      }
      if (response.servidor) {
        localStorage.setItem('servidorData', JSON.stringify(response.servidor));
      }

      const servidorId = response.servidor?.id;
      if (!servidorId) throw new Error('ID do servidor não retornado pela API');

      toast.success('Acesso liberado!', { id: 'validate-code' });
      window.location.href = `/documentos/${servidorId}`;
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Código inválido ou expirado';
      setError(msg);
      toast.error(msg, { id: 'validate-code' });
      setLoading(false);
    }
  };

  // ── Resend code ───────────────────────────────────────────────────────────
  const handleReenviarCodigo = async () => {
    const cleanedCPF = cpf.replace(/\D/g, '');
    if (cleanedCPF.length !== 11) return;
    try {
      toast.loading('Reenviando código...', { id: 'resend-code' });
      await solicitarCodigoAcessoDocumentos(cleanedCPF);
      setCodigo(['', '', '', '', '', '']);
      setError('');
      toast.success('Código reenviado! Verifique seu email.', { id: 'resend-code' });
      document.getElementById('code-input-0')?.focus();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao reenviar código', { id: 'resend-code' });
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <Box sx={{ maxWidth: 520, mx: 'auto' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>

          {/* STEP 1: CPF */}
          {step === 'cpf' && (
            <>
              <DescriptionIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="primary.main" fontWeight="bold">
                Acessar Meus Documentos
              </Typography>
              <Typography variant="body2" paragraph color="text.secondary">
                Digite seu CPF para receber um código de acesso.
              </Typography>

              <form onSubmit={handleSolicitarCodigo}>
                <TextField
                  fullWidth
                  label="CPF"
                  value={cpf}
                  onChange={handleCPFChange}
                  placeholder="000.000.000-00"
                  variant="outlined"
                  margin="normal"
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2 },
                  }}
                  helperText="Use o mesmo CPF da consulta de valores"
                />

                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2, textAlign: 'left' }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || cpf.replace(/\D/g, '').length !== 11}
                  sx={{ mt: 3, py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
                  startIcon={
                    loading
                      ? <CircularProgress size={20} color="inherit" />
                      : <EmailIcon />
                  }
                >
                  {loading ? 'Enviando...' : 'Receber Código'}
                </Button>
              </form>
            </>
          )}

          {/* STEP 2: CÓDIGO */}
          {step === 'codigo' && (
            <>
              <KeyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="primary.main" fontWeight="bold">
                Inserir Código de Acesso
              </Typography>

              {servidorInfo && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #90caf9',
                  }}
                >
                  <Typography variant="body2" color="#1565c0" gutterBottom>
                    Código enviado para: <strong>{servidorInfo.email}</strong>
                  </Typography>
                  <Typography variant="body2" color="#1565c0">
                    Olá, <strong>{servidorInfo.nome_completo}</strong>
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Digite o código de 6 dígitos recebido.
              </Typography>

              <form onSubmit={handleValidarCodigo}>
                <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                  {codigo.map((digit, index) => (
                    <Grid item key={index}>
                      <TextField
                        id={`code-input-${index}`}
                        value={digit}
                        onChange={(e) => handleCodigoChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: 'center',
                            fontSize: '1.6rem',
                            fontWeight: 'bold',
                            padding: '10px 0',
                            width: 38,
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: digit ? 'primary.light' : 'background.paper',
                            transition: 'background-color 0.2s',
                          },
                        }}
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    </Grid>
                  ))}
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}>
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || codigo.join('').length !== 6}
                  sx={{ py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
                  startIcon={
                    loading ? <CircularProgress size={20} color="inherit" /> : null
                  }
                >
                  {loading ? 'Validando...' : 'Acessar Meus Documentos'}
                </Button>
              </form>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  onClick={handleReenviarCodigo}
                  variant="text"
                  size="small"
                  disabled={loading}
                  color="primary"
                >
                  Não recebeu o código? Reenviar
                </Button>
                <Button
                  onClick={() => {
                    setStep('cpf');
                    setCodigo(['', '', '', '', '', '']);
                    setError('');
                  }}
                  variant="text"
                  size="small"
                  disabled={loading}
                  sx={{ color: 'text.secondary' }}
                >
                  ← Usar outro CPF
                </Button>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                O código expira em <strong>15 minutos</strong>
              </Typography>
            </>
          )}

        </Paper>
      </Box>
    </Fade>
  );
};

export default DocumentAuthSimple;